import dayjs from 'dayjs';
import * as Yup from 'yup';
import { encryptStorage } from '../CustomStorage';
import {
  APP_CONSTANTS,
  passwordRules,
  USER_LEVEL_TYPES
} from '../URLConstants';

export function toLowerCase(str) {
  if (str !== undefined && str !== null) return str.toLowerCase();
}

export function titleCase(str) {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatTime(raw) {
  if (!raw) return null;
  if (typeof raw !== 'string') raw = String(raw);

  // 4 digits → HHMM
  if (raw.length === 4) return `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;

  // 6 digits → HHMMSS
  if (raw.length === 6) return `${raw.slice(0, 2)}:${raw.slice(2, 4)}:${raw.slice(4, 6)}`;

  return raw; // fallback
}

export function formatTooltipText(parsed) {
  if (!parsed) return '';

  return Object.entries(parsed)
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value || '--'}`)
    .join('\n');
}

export function parseCourierData(partner, data) {
  if (!partner || !data) return {};

  // Root (Shipment/block)
  const root = data.Shipment || data.shipment || data?.statustracking?.[0]?.Shipment || {};

  // --------------- DTDC ---------------
  // if (partner === 'DTDC') {
  //   const scan = data?.shipmentStatus?.[0] || {};

  //   return {
  //     Status_Code: scan.strAction || null,
  //     Scan_Location: scan.strOrigin || null,
  //     Description: scan.strActionDesc || null,
  //     Remarks: scan.strRemarks || null,
  //     Scan_Date_And_Time: formatDtdcDateTime(scan.strActionDate, scan.strActionTime)
  //   };
  // }

  // --------------- BLUEDART ---------------
  if (partner === 'BlueDart') {
    const scan = root?.Scans?.ScanDetail?.[0] || {};

    return {
      ExpectedDeliveryDate: root.ExpectedDeliveryDate || null,
      Scan: scan.Scan || null,
      Status_Code:
        scan.ScanCode && scan.ScanGroupType ? `${scan.ScanCode}-${scan.ScanGroupType}` : null,
      Scan_Date: scan.ScanDate || null,
      Scan_Time: formatTime(scan.ScanTime),
      Scanned_Location: scan.ScannedLocation || null,
      Comments: scan.Comments || null
    };
  }

  // --------------- DELHIVERY ---------------
  if (partner === 'Delhivery') {
    const st = root.Status || {};

    return {
      PickUp_Date: root.PickUpDate || null,
      Status_Code: root.NSLCode || null,
      Status: st.Status || null,
      Status_Date_And_Time: st.StatusDateTime
        ? new Date(st.StatusDateTime).toLocaleString('en-IN')
        : null,
      Status_Location: st.StatusLocation || null,
      Instructions: st.Instructions || null
    };
  }

  // Unknown partner
  return {};
}

export function extractHierarchyInfoFromPath(taxonomyPath) {
  if (!taxonomyPath) return {};

  const pathParts = taxonomyPath.split('.');
  const result = {};

  const normalize = (value) => (value ? value.replace(/_/g, ' ') : value);

  for (let i = 0; i < pathParts.length - 1; i += 1) {
    const currentPart = pathParts[i].toLowerCase();
    const nextPart = pathParts[i + 1];

    switch (currentPart) {
      case 'ho':
        result.headOffice = normalize(nextPart);
        break;

      case 'zo':
        result.zoneOffice = normalize(nextPart);
        break;

      case 'ro':
        result.regionalOffice = normalize(nextPart);
        break;

      case 'bo':
        result.branchId = nextPart ? nextPart.toUpperCase() : null;
        break;

      default:
        break;
    }
  }

  return result;
}

export function removeNulls(obj) {
  if (Array.isArray(obj)) {
    return obj
      .map((v) => (typeof v === 'object' && v !== null ? removeNulls(v) : v))
      .filter((v) => v !== null && v !== undefined && v !== '');
  }

  if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => [k, typeof v === 'object' ? removeNulls(v) : v])
    );
  }

  return obj;
}

export function getAdminRoleFromHierarchy(path) {
  const info = extractHierarchyInfoFromPath(path);

  if (info.headOffice && !info.regionalOffice && !info.zoneOffice) {
    return 'HOAdmin';
  }

  if (info.headOffice && info.zoneOffice && !info.regionalOffice) {
    return 'ZOAdmin';
  }

  if (info.headOffice && info.regionalOffice && info.zoneOffice) {
    return 'ROAdmin';
  }

  return 'Admin'; // in case path doesn't match expected levels
}

// Compute common hierarchy-related flags from a taxonomy path and user role.
// This keeps role logic reusable across components.
export function computeHierarchyMeta(taxonomyPath, userRole) {
  const hierarchyInfo = extractHierarchyInfoFromPath(taxonomyPath);
  const isAdminUser = userRole === APP_CONSTANTS?.USER_ROLE_ADMIN;

  const hierarchyAdminRole = isAdminUser ? getAdminRoleFromHierarchy(taxonomyPath) : null;

  const isHeadOfficeLocked =
    isAdminUser && ['HOAdmin', 'ZOAdmin', 'ROAdmin'].includes(hierarchyAdminRole);
  const isZoneOfficeLocked = isAdminUser && ['ZOAdmin', 'ROAdmin'].includes(hierarchyAdminRole);
  const isRegionalOfficeLocked = isAdminUser && ['ROAdmin'].includes(hierarchyAdminRole);

  return {
    isAdminUser,
    hierarchyInfo,
    hierarchyAdminRole,
    isHeadOfficeLocked,
    isZoneOfficeLocked,
    isRegionalOfficeLocked
  };
}

// Generic helper to match an option from an array by either id or name (case-insensitive)
export function findOptionByNameOrId(options, nameOrId) {
  if (!Array.isArray(options) || !nameOrId) return null;

  const needle = String(nameOrId).trim().toLowerCase();

  return (
    options.find((o) => String(o?.id).trim().toLowerCase() === needle) ||
    options.find((o) => String(o?.name).trim().toLowerCase() === needle) ||
    null
  );
}
export function clearLocalDataOfUserLogins() {
  encryptStorage.setItem(APP_CONSTANTS.L_KEY_OF_USER_LOGGEDIN_STATUS, false);
  encryptStorage.removeItem(APP_CONSTANTS.L_KEY_OF_USER_DATA);
  encryptStorage.removeItem(APP_CONSTANTS.L_KEY_OF_USER_IOT_CLIENT_ID);
  encryptStorage.removeItem(APP_CONSTANTS.L_KEY_OF_USER_ADMIN_SSOTOKEN);
}

export function clearLoginSession() {
  encryptStorage.setItem(APP_CONSTANTS.L_KEY_OF_USER_LOGGEDIN_STATUS, false);
  encryptStorage.setItem(APP_CONSTANTS.L_KEY_OF_USER_ADMIN_SSOTOKEN, null);
  encryptStorage.setItem(APP_CONSTANTS.L_KEY_OF_USER_DATA, null);
}

export function clearLocalDataForSoundBoxLogin() {
  encryptStorage.setItem(APP_CONSTANTS.L_KEY_OF_USER_LOGGEDIN_STATUS, false);
  encryptStorage.removeItem(APP_CONSTANTS.L_KEY_OF_COGNITO_TOKEN);
  window.location.reload();
}

export function isValidURL(str) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i'
  );
  return !!pattern.test(str);
}

export function getRandomColorVariant() {
  const colorVariants = ['primary', 'secondary', 'error', 'danger'];
  return colorVariants[Math.floor(Math.random() * colorVariants.length)];
}

export function showFieldValueWithCheck(value) {
  return value !== undefined && value !== '' && value !== null ? value : '---';
}

export const showLanguageLabelUsingCode = (languageCode, languageList) => {
  if (!languageCode) return '---';

  const normalizedCode = languageCode.toString().toLowerCase();

  if (languageList?.length) {
    const matchedLanguage = languageList.find(
      (lang) => lang.shortCode?.toLowerCase() === normalizedCode
    );

    if (matchedLanguage?.language) {
      return matchedLanguage.language;
    }
  }

  const fallbackMap = {
    en: 'English',
    english: 'English',

    hi: 'Hindi',
    hindi: 'Hindi',

    bn: 'Bengali',
    bengali: 'Bengali',

    ta: 'Tamil',
    tamil: 'Tamil',

    te: 'Telugu',
    telugu: 'Telugu',
    telgu: 'Telugu',

    mr: 'Marathi',

    pu: 'Punjabi',

    gj: 'Gujarati',

    ml: 'Malayalam'
  };

  return fallbackMap[normalizedCode] || '---';
};

export function getTransactionAnnouncedOrNot(value) {
  if (
    value === '' ||
    value === undefined ||
    value === null ||
    value === 'NULL' ||
    value === 'null'
  ) {
    return 'NA';
  }

  switch (value.toLowerCase()) {
    case 'initialized':
      return '---';
    case 'completed':
      return 'YES';
    case 'failed':
      return 'NO';
    case 'processing':
      return 'Processing';
    default:
      return value;
  }
}

export function showTextOnUIwithDash(value) {
  if (
    value === '' ||
    value === undefined ||
    value === null ||
    value === 'Null' ||
    value === 'null' ||
    value === 'Not Available'
  ) {
    return '---';
  }
  return value;
}

export function getIdTextWidthDots(idStr) {
  if (idStr === null || idStr === undefined || idStr === '') return '';

  if (idStr.length > 5) {
    const lastFiveChars = idStr.slice(-5);
    return 'X'.repeat(idStr.length / 3) + lastFiveChars;
  }

  return idStr;
}

export function crossMarkString(inputString) {
  if (inputString === null || inputString === undefined) return '';

  if (inputString.length > 6) {
    const lastFiveChars = inputString.slice(-6);
    return 'X'.repeat(5) + lastFiveChars;
  }

  return inputString;
}

export function crossMarkStringTransId(inputString) {
  if (inputString === null || inputString === undefined) return '';

  if (inputString.length > 6) {
    const lastFiveChars = inputString.slice(-6);
    return 'X'.repeat(3) + lastFiveChars;
  }

  return inputString;
}

export function crossMarkDeviceIdString(inputString) {
  if (inputString === null || inputString === undefined) return '';

  if (inputString.length > 4) {
    const lastFiveChars = inputString.slice(-4);
    return 'X'.repeat(3) + lastFiveChars;
  }

  return inputString;
}

export function isValidJSON(data) {
  try {
    JSON.parse(data);
    return true;
  } catch (e) {
    return false;
  }
}

export function parseValidationErrorMessage(error) {
  const data = error?.response?.data;

  if (data) {
    if ('validationErrors' in data) {
      if ('message' in data.validationErrors) return data.validationErrors.message;
      return JSON.stringify(data.validationErrors.errors, null, 2);
    }

    if ('message' in data) {
      if ('serverMessage' in data) {
        return `${data.message}\n${JSON.stringify(data.serverMessage, null, 2)}`;
      }
      return data.message;
    }

    if (data.error) {
      if (data.error.error) return data.error.error;
      return JSON.stringify(data.error, null, 2);
    }

    if ('errors' in data) {
      // Convert object to string to avoid React render error
      if (typeof data.errors === 'object') {
        return JSON.stringify(data.errors, null, 2);
      }
      return data.errors;
    }
  }

  return 'Bad Request';
}

export async function parseValidationErrorMessageWithBlobSupport(error) {
  const responseData = error?.response?.data;
  if (!responseData) return parseValidationErrorMessage(error);

  let normalizedData = responseData;

  if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      normalizedData = text ? JSON.parse(text) : {};
    } catch (_e) {
      return parseValidationErrorMessage(error);
    }
  } else if (typeof responseData === 'string') {
    try {
      normalizedData = JSON.parse(responseData);
    } catch (_e) {
      normalizedData = { message: responseData };
    }
  }

  if (typeof normalizedData?.error === 'string' && normalizedData.error.trim()) {
    return normalizedData.error;
  }

  return parseValidationErrorMessage({
    ...error,
    response: {
      ...error?.response,
      data: normalizedData
    }
  });
}

export function parseValidationErrorMessageForBulkUpload(error) {
  if (error?.response) {
    const { data } = error.response;

    // Case: data.validationErrors.message
    if (data?.validationErrors?.message) {
      return data.validationErrors.message;
    }

    // Case: data.validationErrors.errors
    if (data?.validationErrors?.errors) {
      return data.validationErrors.errors;
    }

    // Case: data.failedRows + suggestions (bulk upload format)
    if (data?.failedRows?.length) {
      const msg = data?.message || 'Some rows failed to upload.';
      const failedDetails = data.failedRows
        .map((row) => `Row ${row.rowNumber}: ${row.error}`)
        .join('\n');
      const suggestionDetails = data?.suggestions
        ? `\nSuggestions:\n- ${data.suggestions.join('\n- ')}`
        : '';
      return `${msg}\n\n${failedDetails}${suggestionDetails}`;
    }

    // Case: data.message
    if (data?.message) {
      return data.message;
    }

    // Case: data.error.error or data.error
    if (data?.error) {
      const { error: errorDetail } = data;
      if (typeof errorDetail === 'string') {
        return errorDetail;
      }
      if (errorDetail?.error) {
        return errorDetail.error;
      }
      if (Array.isArray(errorDetail)) {
        return errorDetail
          .map((err) => {
            const { rowIndex, reason } = err;
            if (rowIndex !== undefined && reason) {
              return `Row No ${rowIndex} has issue: ${reason}`;
            }
            return JSON.stringify(err);
          })
          .join(' | ');
      }
    }

    // Case: data.errors
    if (data?.errors) {
      return data.errors;
    }
  }

  return 'Bad Request';
}

function collectTrimmedNonEmpty(items) {
  return items.map((item) => String(item).trim()).filter(Boolean);
}

export function normalizeBulkUploadEntryErrors(rawErrors) {
  if (rawErrors == null) return [];

  if (Array.isArray(rawErrors)) {
    return collectTrimmedNonEmpty(rawErrors);
  }

  if (typeof rawErrors === 'object') {
    return collectTrimmedNonEmpty(Object.values(rawErrors).flat());
  }

  const t = String(rawErrors).trim();
  return t ? [t] : [];
}

function normalizeBulkUploadEmail(value) {
  if (value == null || value === '') return '';
  return String(value).trim();
}

export function normalizeFailedUsers(payload) {
  const rawList = payload?.failedUsers ?? payload?.failed_users;
  if (!Array.isArray(rawList)) return [];

  return rawList
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const email = normalizeBulkUploadEmail(entry.email);
      const errors = normalizeBulkUploadEntryErrors(entry.errors);
      if (!email && errors.length === 0) return null;

      return { email, errors };
    })
    .filter(Boolean);
}

export function capitalizeFirstLetter(str) {
  if (str !== undefined && str !== null && str !== '') {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str;
}

export function CapitializeText(str) {
  if (str !== undefined && str !== null && str !== '') {
    return str.toUpperCase();
  }

  return str;
}

export function getUserAciveInactiveBasedColor(status) {
  if (status === null || status === undefined || status === '') return 'info';

  switch (status.toLowerCase()) {
    case 'active':
      return 'info';
    case 'inactive':
      return 'error';
    default:
      return 'info';
  }
}

export function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string?.length; i += 1) {
    // replaced i++ with i += 1
    hash = string?.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    // replaced i++ with i += 1
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value?.toString(16)}`?.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

export function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name)
    },
    children: `${name?.split(' ')[0][0]}${name?.split(' ')?.[1]?.[0]}`
  };
}

export function getBatteryPerceByVBAT(VBAT) {
  if (VBAT !== null && VBAT !== undefined && VBAT !== '') {
    const voltage = Math.min(4.15, Math.max(VBAT, 3.4));
    return parseInt(((voltage - 3.4) * 100) / (4.15 - 3.4), 10);
  }
  return '---';
}

export function getLanguageLetter(key) {
  const letters = {
    en: 'E', // English
    hi: 'ह', // Hindi
    mr: 'म', // Marathi
    gj: 'ગ', // Gujarati
    bn: 'ব', // Bengali
    pu: 'ਪ', // Punjabi
    te: 'త', // Telugu
    ta: 'த', // Tamil
    ml: 'മ', // Malayalam
    od: 'ଓ', // Odia
    ka: 'ಕ', // Kannada
    as: 'অ' // Assamese
  };
  return letters[key] || '?'; // Fallback to "?" if key is not found
}

// Device Command Type Mapping
export const DEVICE_COMMAND_TYPES = {
  DEVICE_CUSTOM_ANNOUNCEMENT: 'Custom Audio Play',
  DEVICE_OTA_UPDATE: 'Firmware Update'
};

export function getDeviceCommandTypeName(commandType) {
  if (!commandType) return '---';
  return DEVICE_COMMAND_TYPES[commandType] || commandType;
}


export const getEntityTypeAndId = (
  values,
  headOfficesList,
  zonesList,
  regionsList,
  branchesList
) => {
  const { branchId, headOfficeName, zoneOfficeName, regionalOfficeName } = values;

  switch (true) {
    case !!(branchId && headOfficeName && zoneOfficeName && regionalOfficeName):
      return {
        userLevel: USER_LEVEL_TYPES?.BO,
        entityId:
          branchesList?.find((item) => item.branchId === branchId)?.id || '---'
      };

    case !!(!branchId && headOfficeName && zoneOfficeName && regionalOfficeName):
      return {
        userLevel: USER_LEVEL_TYPES?.RO,
        entityId:
          regionsList?.find((item) => item.name === regionalOfficeName)?.id || null
      };

    case !!(!branchId && headOfficeName && zoneOfficeName && !regionalOfficeName):
      return {
        userLevel: USER_LEVEL_TYPES?.ZO,
        entityId:
          zonesList?.find((item) => item.name === zoneOfficeName)?.id || null
      };

    case !!(headOfficeName && !zoneOfficeName && !regionalOfficeName && !branchId):
      return {
        userLevel: USER_LEVEL_TYPES?.HO,
        entityId: headOfficesList?.[0]?.id || null
      };

    default:
      return {
        userLevel: null,
        entityId: null
      };
  }
};

// The merchant bulk upload can come back HTTP 200 and still have saved nothing — the body
// carries a per-row `errors` array alongside a summary `message` such as "Saved 0 merchants".
// Different endpoint versions spell the row list differently, so normalise the known shapes
// into the `{ rowIndex, error }` list AlertDialog renders.
export const extractBulkUploadRowErrors = (payload) => {
  const raw = payload?.errors || payload?.failedRows || payload?.validationErrors?.errors;
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => ({
    rowIndex: item?.rowNumber ?? item?.rowIndex ?? index + 1,
    error: item?.error ?? item?.reason ?? item?.message ?? 'Unknown error',
    row: item?.row ?? null
  }));
};

// The `accept=".csv"` hint doesn't stop a user picking another file type via "All files".
export const isCsvFile = (file) => {
  const name = file?.name?.toLowerCase() || '';
  return name.endsWith('.csv') || ['text/csv', 'application/vnd.ms-excel'].includes(file?.type);
};

// A merchant-vpa row keeps the merchant's own fields under `merchantProfile`; lift them to
// the top level so screens built on the older flat merchant shape can read them as before.
export const flattenMerchantVpaRow = (row) => {
  const profile = row?.merchantProfile || {};
  return {
    ...row,
    name: profile.name,
    phone: profile.phone,
    email: profile.email,
    address: profile.address,
    state: profile.state,
    city: profile.city,
    pincode: profile.pincode,
    mid: profile.mid,
    errorMessage: row?.remarks
  };
};

export const getChangedFields = (current, original) =>
  Object.fromEntries(
    Object.entries(current).filter(([key, value]) => {
      const originalValue = original?.[key];

      const normalizedValue = value ?? '';
      const normalizedOriginal = originalValue ?? '';

      return normalizedValue !== normalizedOriginal;
    })
  );


export const getMaxEndDate = (start) => {
  if (!start) return dayjs();

  const startD = start.startOf('day'); 
  const monthSpanEnd = startD.add(1, 'month').subtract(1, 'day');
  const today = dayjs().startOf('day');

  return monthSpanEnd.isAfter(today) ? today : monthSpanEnd;
};


export function getPasswordValidationError(password) {
  if (!password) return 'Password is required';

  const failedRule = passwordRules.find((rule) => !rule.test(password));
  return failedRule ? failedRule.label : null;
}

export function isPasswordStrong(password) {
  return !getPasswordValidationError(password);
}

export function passwordYupField() {
  return Yup.string()
    .required('Password is required')
    .test('password-rules', '', function (value) {
      const errorMessage = getPasswordValidationError(value);
      return errorMessage
        ? this.createError({ message: errorMessage })
        : true;
    });
}

export function phoneYupField() {
  return Yup.string()
    .required('Phone number is required')
    .matches(
      /^(?:\+91[\-\s]?|91[\-\s]?|0)?[6-9]\d{9}$/,
      'Enter a valid Indian phone number'
    );
}

/** Reduces login-credential autofill on admin "set new user password" fields. */
export function applyPasswordAutofillWorkaround(fieldProps = {}) {
  const {
    onFocus,
    slotProps: incomingSlotProps = {},
    ...restProps
  } = fieldProps;

  const handleFocus = (event) => {
    event.target.removeAttribute('readonly');
    onFocus?.(event);
  };

  return {
    ...restProps,
    type: 'password',
    autoComplete: 'new-password',
    onFocus: handleFocus,
    slotProps: {
      ...incomingSlotProps,
      htmlInput: {
        readOnly: true,
        autoComplete: 'new-password',
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
        'data-form-type': 'other',
        'aria-autocomplete': 'none',
        ...incomingSlotProps.htmlInput
      }
    }
  };
}